import * as React from 'react';
import { connect } from 'react-redux';
import { camelCase, isEmpty, isNil } from 'lodash';

import navigate from 'libs/router/navigate';

import SOVRequestAccessPage from 'modules/insights/sov/pages/consumer-insights/access/SOVRequestAccessPage';

import { danger } from 'redux-store/reducers/common-reducer';
import { fetchReport, resetReport } from 'modules/insights/sov/reducers/reports/consumer-insights/sov-reports';
import {
  checkAccess,
  resetAccess,
  updateAccess
} from 'modules/insights/sov/reducers/access/consumer-insights/sov-access';

import { INSTAGRAM } from 'modules/insights/sov/utils/sov';
import {
  ERROR_PAGE_URL,
  getAuthInfo
} from 'modules/insights/sov/utils/consumer-insights/sov-consumer-insights';

import type { LocationWindow, Params } from 'types';
import type { SOVReport } from 'modules/insights/sov/types';
import type {
  SOVGeneralAuthInfo,
  SOVRequestAccessResponse
} from 'modules/insights/sov/types/consumer-insights';

type OwnProps = {
  params: Params,
  location: LocationWindow
};

type StateProps = {
  report: SOVReport,
  accessRequestResponse: SOVRequestAccessResponse
};

type DispatchProps = {
  dispatchFetchReport: (
    authInfo: Partial<SOVGeneralAuthInfo>,
    email: string | undefined,
    name: string | undefined
  ) => Promise<void>,
  dispatchCheckAccess: (authInfo: Partial<SOVGeneralAuthInfo>) => Promise<void>,
  dispatchUpdateAccess: (authInfo: Partial<SOVGeneralAuthInfo>, updatedResponse: SOVRequestAccessResponse) => Promise<void>,
  dispatchErrorPopup: (message: string) => void,
  dispatchResetAccess: () => void,
  dispatchResetReport: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  banner: {
    email?: string,
    name?: string
  }
};

class SOVBasePageConsumerInsights extends React.PureComponent<Props, State> {
  state = {
    banner: {
      email: '',
      name: ''
    }
  };

  componentDidMount() {
    const { dispatchCheckAccess } = this.props;
    const authInfo = this.getAuthValues();

    dispatchCheckAccess(authInfo)
      .catch(this.handleInvalidRedirect);
  }

  componentWillUnmount() {
    const { dispatchResetReport, dispatchResetAccess } = this.props;

    dispatchResetReport();
    dispatchResetAccess();
  }

  getAuthInfo(): any {
    const { params } = this.props;
    const reportId = Number(params.id) || null;
    const authInfo = this.getAuthValues();

    if (isEmpty(authInfo) && !isNil(reportId)) {
      return getAuthInfo(reportId);
    }

    return authInfo;
  }

  getAuthValues(): Partial<SOVGeneralAuthInfo> {
    const { location } = this.props;
    const query = location.query || {};
    const result: Partial<SOVGeneralAuthInfo> = {};
    const keys = Object.keys(query).sort();

    keys.forEach(key => {
      const resultKey = camelCase(key.replace(/rc_/, ''));
      result[resultKey] = query[key];
    });

    return result;
  }

  fetchReport = (): void => {
    const { dispatchFetchReport } = this.props;
    const { banner } = this.state;
    const authInfo = this.getAuthValues();

    if (authInfo.token) {
      dispatchFetchReport(authInfo, banner.email, banner.name)
        .then(this.handleValidRedirect)
        .catch(this.handleInvalidRedirect);

      return;
    }

    this.handleInvalidRedirect();
  };

  handleUpdateRecord(): void {
    const { accessRequestResponse, dispatchUpdateAccess, dispatchErrorPopup } = this.props;
    const { banner } = this.state;
    const authInfo = this.getAuthValues();

    const updatedAccessRequestResponse = {
      ...accessRequestResponse,
      contactEmail: banner.email,
      contactName: banner.name
    };

    dispatchUpdateAccess(authInfo, updatedAccessRequestResponse)
      .then(this.fetchReport)
      .catch(error => {
        const errorMsg = error?.response?.data?.detail || '';
        dispatchErrorPopup(errorMsg);
      });
  }

  handleValidRedirect = (): void => {
    const { report } = this.props;
    const reportId = report.id;

    if (reportId) {
      navigate.to(`/consumer-insights/sov/${report.reportType}/${reportId}`);
    }
  };

  handleInvalidRedirect(): void {
    navigate.to(ERROR_PAGE_URL);
  }

  handleRequestAccessFieldChange = (name: string, value: string): void => {
    this.setState(state => ({
      ...state,
      banner: {
        ...state.banner,
        [name]: value
      }
    }));
  };

  handleRequestAccessSubmit = (): void => {
    const { accessRequestResponse } = this.props;
    const reportId = accessRequestResponse.reportId;

    // new report
    if (isNil(reportId)) {
      this.fetchReport();
      return;
    }

    // existing report but without contact details
    this.handleUpdateRecord();
  };

  render(): React.ReactNode {
    const { accessRequestResponse } = this.props;

    if (isEmpty(accessRequestResponse)) {
      return null;
    }

    if (!accessRequestResponse.contactEmail || !accessRequestResponse.contactName) {
      return (
        <div className="sov-base-page-consumer-insights__banner">
          <SOVRequestAccessPage
            onFieldChange={this.handleRequestAccessFieldChange}
            onSubmit={this.handleRequestAccessSubmit}
          />
        </div>
      );
    }

    // report exists and contact details have already been added
    // it's safe to call here b/c all outcomes lead to redirect
    this.fetchReport();

    return null;
  }
}

const mapStateToProps = ({ sovReportsConsumerInsights, sovAccessConsumerInsights }): StateProps => ({
  report: sovReportsConsumerInsights.report,
  accessRequestResponse: sovAccessConsumerInsights.accessRequestResponse
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchReport: (
    authInfo: Partial<SOVGeneralAuthInfo>,
    email: string | undefined,
    name: string | undefined
  ) => (
    dispatch(fetchReport(authInfo, INSTAGRAM, email, name))
  ),
  dispatchCheckAccess: (authInfo: Partial<SOVGeneralAuthInfo>) => (
    dispatch(checkAccess(authInfo))
  ),
  dispatchUpdateAccess: (authInfo: Partial<SOVGeneralAuthInfo>, updatedResponse: SOVRequestAccessResponse) => (
    dispatch(updateAccess(authInfo, updatedResponse))
  ),
  dispatchErrorPopup: (message: string) => (
    dispatch(danger(message))
  ),
  dispatchResetAccess: () => (
    dispatch(resetAccess())
  ),
  dispatchResetReport: () => (
    dispatch(resetReport())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(SOVBasePageConsumerInsights);
